import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Select from 'react-select';

const MOBILE_BREAKPOINT = 576;

/** 드롭다운 메뉴 최대 너비 (초과 시 옵션 텍스트 줄바꿈) */
const MENU_MAX_WIDTH = 420;

/**
 * CustomSelect
 * - react-select wrapper
 * - focus styles: 기본(bootstrap-like) 색상 사용 (테마색 미사용)
 * - value: primitive option.value
 * - options: [{ value, label }]
 * - onChange: (nextValue) => void
 * - 메뉴 너비: 기본은 컨트롤 너비와 동일, 긴 항목이 있으면 그만큼 확장하되 max-width 초과 시 옵션 줄바꿈
 */
export default function CustomSelect({
  value,
  options,
  onChange,
  placeholder,
  isSearchable = true,
  isMulti = false,
  isClearable = false,
  isDisabled = false,
  inputId,
  classNamePrefix = 'custom-select',
  menuPortalTarget,
  styles: userStyles,
  onMenuOpen: onMenuOpenUser,
  onMenuClose: onMenuCloseUser,
  ...rest
}) {
  const containerRef = useRef(null);
  const [menuControlWidth, setMenuControlWidth] = useState(null);
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`).matches
  );

  useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT}px)`);
    const handler = () => setIsMobile(mql.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const selectedOption = useMemo(() => {
    const list = Array.isArray(options) ? options : [];
    return list.find((o) => o?.value === value) || null;
  }, [options, value]);

  const selectedOptions = useMemo(() => {
    const list = Array.isArray(options) ? options : [];
    const values = Array.isArray(value) ? value : [];
    // freeform 값(= options에 없는 값)도 multi chip으로 보이도록 보정
    return values.map((v) => list.find((o) => o?.value === v) || { value: v, label: String(v) });
  }, [options, value]);

  const resolvedMenuPortalTarget = useMemo(() => {
    if (menuPortalTarget !== undefined) return menuPortalTarget;
    if (typeof document === 'undefined') return undefined;
    return document.body;
  }, [menuPortalTarget]);

  const onMenuOpen = useCallback(() => {
    const w = containerRef.current?.offsetWidth;
    setMenuControlWidth(typeof w === 'number' && w > 0 ? w : null);
    onMenuOpenUser?.();
  }, [onMenuOpenUser]);

  const onMenuClose = useCallback(() => {
    setMenuControlWidth(null);
    onMenuCloseUser?.();
  }, [onMenuCloseUser]);

  const styles = useMemo(() => {
    const isTouchDevice = typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(hover: none) and (pointer: coarse)').matches
      : false;
    const mobileFontSize = isTouchDevice ? 16 : undefined;
    const baseStyles = {
      control: (base, state) => ({
        ...base,
        minHeight: 38,
        borderRadius: 6,
        fontSize: mobileFontSize || base.fontSize,
        backgroundColor: state.isDisabled ? 'var(--theme-color-surface-light, #f8f9fa)' : '#fff',
        // bootstrap 기본 포커스 색상 계열
        borderColor: state.isFocused ? '#86b7fe' : 'var(--theme-color-border, #ced4da)',
        boxShadow: state.isFocused ? '0 0 0 0.25rem rgba(13,110,253,.25)' : 'none',
        ':hover': {
          borderColor: state.isFocused ? '#86b7fe' : 'var(--theme-color-border-strong, #adb5bd)',
        },
      }),
      valueContainer: (base) => ({ ...base, padding: '0 12px', fontSize: mobileFontSize || base.fontSize }),
      input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: mobileFontSize || base.fontSize }),
      indicatorsContainer: (base) => ({ ...base, height: 38 }),
      indicatorSeparator: (base) => ({ ...base, display: 'none' }),
      dropdownIndicator: (base) => ({
        ...base,
        color: 'var(--theme-color-text-muted, #6c757d)',
        ':hover': { color: 'var(--theme-color-text-strong, #212529)' },
      }),
      clearIndicator: (base) => ({
        ...base,
        color: 'var(--theme-color-text-muted, #6c757d)',
        ':hover': { color: 'var(--theme-color-text-strong, #212529)' },
      }),
      placeholder: (base) => ({
        ...base,
        color: 'var(--theme-color-text-muted, #6c757d)',
        fontSize: mobileFontSize || base.fontSize,
      }),
      singleValue: (base) => ({
        ...base,
        color: 'var(--theme-color-text-strong, #212529)',
        fontSize: mobileFontSize || base.fontSize,
      }),
      menu: (base) => ({
        ...base,
        zIndex: 9999,
        minWidth: menuControlWidth != null && menuControlWidth > 0 ? menuControlWidth : 200,
        width: 'max-content',
        maxWidth: MENU_MAX_WIDTH,
      }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isSelected
          ? 'rgba(13,110,253,.10)'
          : state.isFocused
            ? 'var(--theme-color-surface-light, #f8f9fa)'
            : '#fff',
        color: 'var(--theme-color-text-strong, #212529)',
        cursor: 'pointer',
        fontSize: mobileFontSize || base.fontSize,
        whiteSpace: 'normal',
        // wordBreak: 'break-word',
      }),
    };

    if (!userStyles) return baseStyles;
    return { ...baseStyles, ...userStyles };
  }, [userStyles, menuControlWidth]);

  const containerStyle = isMobile
    ? { width: '100%' }
    : { width: 'auto' };

  return (
    <div ref={containerRef} style={containerStyle}>
      <Select
        inputId={inputId}
        isMulti={isMulti}
        value={isMulti ? selectedOptions : selectedOption}
        options={options}
        onChange={(opt) => {
          if (isMulti) {
            const next = Array.isArray(opt) ? opt.map((o) => o?.value).filter((v) => v != null) : [];
            onChange?.(next);
            return;
          }
          onChange?.(opt?.value ?? '');
        }}
        placeholder={placeholder}
        isSearchable={isSearchable}
        isClearable={isClearable}
        isDisabled={isDisabled}
        classNamePrefix={classNamePrefix}
        menuPortalTarget={resolvedMenuPortalTarget}
        menuShouldScrollIntoView={true}
        menuPosition={resolvedMenuPortalTarget ? 'fixed' : 'absolute'}
        onMenuOpen={onMenuOpen}
        onMenuClose={onMenuClose}
        styles={styles}
        {...rest}
      />
    </div>
  );
}

